import React, { useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { refusalFor, storyRefusal } from '../services/access';
import { cityTime } from '../city/cityTime';

// AccessRefusal — what a closed door says.
//
// The RULE lives on the room and the CHECK lives in enterInterior; this is only
// the voice. Keeping them apart is what lets the hall stay shut when the guard
// has not loaded, been moved, or been deleted — and lets him be replaced by a
// velvet rope, a locked gate or a bouncer with dialogue without touching the
// rule he is enforcing.
//
// ── WHY IT TIMES OUT ─────────────────────────────────────────────────────────
// Because the refusal is a reaction to an ACTION, and the action is walking into
// a trigger volume. Leave it up and it becomes furniture: you walk away, it is
// still there, and the next time it appears you have already stopped reading it.
// It also has to clear itself, since walking off is the most likely response and
// nobody should have to dismiss a doorman.
const HOLD_MS = 9000;
// A shut door is a fact, not a sales pitch: a small note, gone sooner.
const CLOSED_MS = 4500;

const wrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  bottom: 'max(96px, calc(env(safe-area-inset-bottom) + 96px))', zIndex: 74,
  width: 'min(420px, calc(100vw - 32px))',
  padding: '14px 16px 13px', borderRadius: 16,
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(125,211,255,0.4)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
  fontFamily: 'system-ui, sans-serif', color: '#e9f6ff',
  pointerEvents: 'auto',
};
const who = { font: '700 11px system-ui', letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(125,211,255,0.85)', marginBottom: 6 };
const line = { font: '500 15px/1.45 system-ui', marginBottom: 12 };
const row = { display: 'flex', gap: 8, alignItems: 'center' };
const cta = {
  flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
  font: '700 14px system-ui', background: 'linear-gradient(180deg,#7dd3ff,#4aa8e0)', color: '#04131c',
  textAlign: 'center', textDecoration: 'none',
};
const dismiss = {
  padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.18)', background: 'transparent',
  color: 'rgba(233,246,255,0.75)', font: '600 14px system-ui',
};

export default function AccessRefusal() {
  const refusal = useWorldStore((s) => s.refusal);
  const clearRefusal = useWorldStore((s) => s.clearRefusal);

  // Keyed on `at` rather than on the object: bouncing off the same door twice
  // should restart the clock, and without the timestamp the second attempt
  // produces an identical object that React sees as no change at all.
  useEffect(() => {
    if (!refusal) return undefined;
    const t = setTimeout(clearRefusal, refusal.closed ? CLOSED_MS : HOLD_MS);
    return () => clearTimeout(t);
  }, [refusal?.at, clearRefusal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!refusal) return null;
  // THE CLOCK SAID NO (a venue's hours, worldStore.enterInterior). No guard and
  // nothing to buy — just when to come back, on the city's own clock with its
  // zone (the city is Oakland; a visitor anywhere else needs to know that).
  if (refusal.closed) {
    const when = refusal.closed.opensAt ? `opens at ${cityTime(refusal.closed.opensAt, { zone: true })}` : 'come back later';
    return (
      <div style={{ ...wrap, width: 'auto', maxWidth: 'calc(100vw - 32px)', padding: '10px 16px', borderColor: 'rgba(255,190,120,0.35)', pointerEvents: 'none' }}>
        <div style={{ font: '600 14px/1.4 system-ui', color: '#ffe9d2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ROOMS[refusal.room]?.name || 'This place'} · Closed — {when}
        </div>
      </div>
    );
  }
  // Two doormen, one voice. A story refusal names the NEXT thing to do; the
  // ladder refusal names what to buy.
  const { line: says, cta: label, url } = refusal.story
    ? storyRefusal(refusal.story)
    : refusalFor(refusal.has, refusal.needs);
  const room = ROOMS[refusal.room]?.name || 'this place';

  return (
    <div style={wrap}>
      <div style={who}>Security · {room}</div>
      <div style={line}>{says}</div>
      <div style={row}>
        {url ? (
          // A new tab, not a navigation. Leaving the page unloads the world —
          // the city, the models and wherever they were standing — and a player
          // sent to a pricing page should be able to come back to the door they
          // were turned away from rather than to the loading screen.
          <a style={cta} href={url} target="_blank" rel="noopener noreferrer" onClick={clearRefusal}>{label}</a>
        ) : (
          <div style={{ ...cta, background: 'rgba(255,255,255,0.08)', color: 'rgba(233,246,255,0.8)', cursor: 'default' }}>{label}</div>
        )}
        <button style={dismiss} onClick={clearRefusal}>Not now</button>
      </div>
    </div>
  );
}
